import React, { useState } from 'react';
import api from '../../api/api';
import { Clock, Megaphone, Users, Briefcase, Handshake } from 'lucide-react';
import { SetupStepCard, SquareOptionCard } from './SetupCards';
import MicTester from './MicTester';
import PrePitchModal from './PrePitchModal';
import { PITCH_TYPE_LABELS } from './pitchShared';

export default function PitchSetupTab({ isDarkMode, setView, setHistoryKind, remainingAttempts }) {
  const [isReady, setIsReady] = useState(false);
  const [showPreModal, setShowPreModal] = useState(false);
  const [sessionSetUpLoading, setSessionSetUpLoading] = useState(false);

  const [duration, setDuration] = useState(60); // 60 | 90
  const [pitchType, setPitchType] = useState(''); // networking | recruiter | investor
  const [targetRole, setTargetRole] = useState('');

  const inputClass = `mt-2 w-full max-w-md text-sm rounded-lg border px-3 py-2 ${
    isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-300 text-slate-700 placeholder-slate-400'
  }`;

  const handleStartPitch = async () => {
    setSessionSetUpLoading(true);
    // Open synchronously (pre-await) so Safari doesn't treat this as a popup and block it
    const sessionTab = window.open('', '_blank');
    try {
      // ASSUMED CONTRACT: POST /pitch/sessions -> { pitchPracticeId, startTime }
      const res = await api.post('/pitch/sessions', {
        durationSeconds: duration,
        pitchType,
        targetRole: targetRole.trim() || null,
      });
      const id = res.data.pitchPracticeId;
      // redirect the pre-opened tab now that we have the session ID
      if (sessionTab) {
        sessionTab.location.href = `/pitchSession?session=${id}`;
      } else {
        window.location.href = `/pitchSession?session=${id}`;
      }
      setHistoryKind('pitch');
      setView('history');
      setShowPreModal(false);
    } catch (err) {
      console.log('Error starting pitch session', err);
      sessionTab?.close();
    } finally {
      setSessionSetUpLoading(false);
    }
  };

  const canStart = !!duration && !!pitchType && isReady;
  const outOfAttempts = remainingAttempts != null && remainingAttempts <= 0; // null = still loading, not zero
  const step3Locked = !pitchType;

  return (
    <div className="relative p-6 md:p-10 max-w-3xl mx-auto">
      <div
        className={`space-y-6 transition-[filter] ${outOfAttempts ? 'blur-sm pointer-events-none select-none' : ''}`}
        aria-hidden={outOfAttempts}
      >
        <div>
          <h2 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
            Set up your elevator pitch practice
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Pick a length and a setting, check your mic, then record one take.
          </p>
        </div>

        <SetupStepCard step={1} title="Pitch Length" isDarkMode={isDarkMode}>
          <div className="flex flex-wrap gap-4 justify-center">
            <SquareOptionCard
              value={30}
              label="30 seconds"
              Icon={Clock}
              description="Ultra-tight — just the hook and the ask, nothing else."
              selected={duration === 30}
              onSelect={setDuration}
              isDarkMode={isDarkMode}
            />
            <SquareOptionCard
              value={60}
              label="60 seconds"
              Icon={Clock}
              description="Tight and punchy — hook, who you are, what you offer, the ask."
              selected={duration === 60}
              onSelect={setDuration}
              isDarkMode={isDarkMode}
            />
            <SquareOptionCard
              value={90}
              label="90 seconds"
              Icon={Clock}
              description="A little more room — add a brief proof point or short story."
              selected={duration === 90}
              onSelect={setDuration}
              isDarkMode={isDarkMode}
            />
          </div>
        </SetupStepCard>

        <SetupStepCard step={2} title="Where's this pitch for?" isDarkMode={isDarkMode}>
          <div className="flex flex-wrap gap-4 justify-center mb-4">
            <SquareOptionCard
              value="networking"
              label={PITCH_TYPE_LABELS.networking}
              Icon={Handshake}
              description="A career fair or networking event — warm, memorable, a soft ask to connect."
              selected={pitchType === 'networking'}
              onSelect={setPitchType}
              isDarkMode={isDarkMode}
            />
            <SquareOptionCard
              value="recruiter"
              label={PITCH_TYPE_LABELS.recruiter}
              Icon={Users}
              description="A recruiter screen — tight relevance to a role, quantified results, a clear ask."
              selected={pitchType === 'recruiter'}
              onSelect={setPitchType}
              isDarkMode={isDarkMode}
            />
            <SquareOptionCard
              value="investor"
              label={PITCH_TYPE_LABELS.investor}
              Icon={Briefcase}
              description="An investor or startup context — problem, why-you / why-now, a specific next step."
              selected={pitchType === 'investor'}
              onSelect={setPitchType}
              isDarkMode={isDarkMode}
            />
          </div>
          <label className={`block text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Target role <span className="font-normal normal-case">(optional — helps tailor the feedback)</span>
          </label>
          <input
            type="text"
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
            placeholder="e.g. Data Analyst"
            className={inputClass}
          />
        </SetupStepCard>

        <SetupStepCard step={3} title="Mic Check" isDarkMode={isDarkMode} locked={step3Locked}>
          <MicTester isDarkMode={isDarkMode} setIsReady={setIsReady} />
        </SetupStepCard>

        <div className="flex justify-center pt-2">
          <button
            type="button"
            disabled={!canStart || outOfAttempts}
            onClick={() => setShowPreModal(true)}
            className={`px-8 py-3 rounded-xl text-sm font-semibold transition-colors ${
              canStart && !outOfAttempts
                ? 'bg-primary-600 hover:bg-primary-500 text-white'
                : (isDarkMode ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed')
            }`}
          >
            Ready to Record Your Pitch
          </button>
        </div>
      </div>

      {outOfAttempts && (
        <div className="absolute inset-0 z-10 flex items-start justify-center p-6">
          <div className={`sticky top-24 max-w-md text-center rounded-2xl border shadow-xl p-6 ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
          }`}>
            <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
              isDarkMode ? 'bg-red-400/10' : 'bg-red-500/10'
            }`}>
              <Megaphone size={22} className={isDarkMode ? 'text-red-400' : 'text-red-600'} />
            </div>
            <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
              You've reached your attempt limit
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Your limit will reset when your subscription renews.
            </p>
          </div>
        </div>
      )}

      <PrePitchModal
        isOpen={showPreModal}
        onClose={() => setShowPreModal(false)}
        isDarkMode={isDarkMode}
        sessionSetUpLoading={sessionSetUpLoading}
        onStart={handleStartPitch}
      />
    </div>
  );
}
